/**
 * Privy Slice
 *
 * Manages privy level, state, and verification data.
 * Reads privy information from auth JWT claims and fetches verification status.
 *
 * GW-6769: idvStatus and privy2Status now reflect gw-signals-api response shapes.
 */

import { setLoading, setLoaded, setError, clearError as clearOpError } from '@gw/ui';

import { ApiError } from '@/api/apiError';
import { privyApi } from '@/api/privy';
import { privy2Api } from '@/api/privy2';
import { isPrivyEligible } from '@/utils/privy.util';

import type {
  LoadingStates,
  LoadedTimestamps,
  ErrorStates,
  PrivyLevel,
  PrivyState,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

/**
 * Normalized IDV session status stored in the slice.
 * hasActiveSession is derived: true when the status endpoint returns 200,
 * false when it returns 404 (no session).
 */
export interface IdvStatus {
  hasActiveSession: boolean;
  sessionStatus?: string;
}

/**
 * Normalized BGC session status stored in the slice.
 * hasActiveSession is derived: true when the status endpoint returns 200,
 * false when it returns 404 (no session).
 */
export interface BgcStatus {
  hasActiveSession: boolean;
  sessionStatus?: string;
  invitationUrl?: string;
}

export interface PrivySlice {
  // State
  privyLevel: PrivyLevel | null;
  privyState: PrivyState | null;
  idvStatus: IdvStatus | null;
  privy2Status: BgcStatus | null;
  privy2InvitationUrl: string | null;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;

  // Selectors
  isEligible: () => boolean;

  // Actions
  fetchIdvStatus: () => Promise<void>;
  fetchPrivy2Status: () => Promise<void>;
  initiatePrivy2Check: () => Promise<void>;
  setPrivyFromAuth: (privyLevel?: PrivyLevel, privyState?: PrivyState) => void;
  clearError: (operation?: string) => void;
}

const initialPrivyState = {
  privyLevel: null,
  privyState: null,
  idvStatus: null,
  privy2Status: null,
  privy2InvitationUrl: null,
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
};

export const createPrivySlice: StateCreator<PrivySlice, [], [], PrivySlice> = (set, get) => ({
  ...initialPrivyState,

  // Selectors
  isEligible: () => {
    return isPrivyEligible(get().privyLevel);
  },

  fetchIdvStatus: async () => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchIdvStatus', true),
      errors: clearOpError(currentState.errors, 'fetchIdvStatus'),
    });

    try {
      const response = await privyApi.fetchPrivyStatus();

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchIdvStatus', true);

      set({
        idvStatus: {
          hasActiveSession: true,
          sessionStatus: response.sessionStatus,
        },
        isLoading: setLoading(state.isLoading, 'fetchIdvStatus', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();

      // 404 means no IDV session exists — not an error state, just no session
      if (error instanceof ApiError && error.status === 404) {
        const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchIdvStatus', true);
        set({
          idvStatus: { hasActiveSession: false },
          isLoading: setLoading(state.isLoading, 'fetchIdvStatus', false),
          hasLoaded: loadedState.hasLoaded,
          lastLoaded: loadedState.lastLoaded,
        });
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchIdvStatus', false);

      set({
        isLoading: setLoading(state.isLoading, 'fetchIdvStatus', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: setError(state.errors, 'fetchIdvStatus', errorMessage),
      });
    }
  },

  fetchPrivy2Status: async () => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchPrivy2Status', true),
      errors: clearOpError(currentState.errors, 'fetchPrivy2Status'),
    });

    try {
      const response = await privy2Api.getPrivy2Status();

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchPrivy2Status', true);

      set({
        privy2Status: {
          hasActiveSession: response.hasActiveSession,
          sessionStatus: response.sessionStatus,
          invitationUrl: response.invitationUrl,
        },
        privy2InvitationUrl: response.invitationUrl ?? null,
        isLoading: setLoading(state.isLoading, 'fetchPrivy2Status', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();

      // 404 means no BGC session exists — not an error state, just no session
      if (error instanceof ApiError && error.status === 404) {
        const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchPrivy2Status', true);
        set({
          privy2Status: { hasActiveSession: false },
          isLoading: setLoading(state.isLoading, 'fetchPrivy2Status', false),
          hasLoaded: loadedState.hasLoaded,
          lastLoaded: loadedState.lastLoaded,
        });
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchPrivy2Status', false);

      set({
        isLoading: setLoading(state.isLoading, 'fetchPrivy2Status', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: setError(state.errors, 'fetchPrivy2Status', errorMessage),
      });
    }
  },

  initiatePrivy2Check: async () => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'initiatePrivy2Check', true),
      errors: clearOpError(currentState.errors, 'initiatePrivy2Check'),
    });

    try {
      const response = await privy2Api.initiatePrivy2();

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'initiatePrivy2Check', true);

      set({
        privy2Status: {
          hasActiveSession: true,
          sessionStatus: 'pending',
          invitationUrl: response.invitationUrl,
        },
        privy2InvitationUrl: response.invitationUrl,
        isLoading: setLoading(state.isLoading, 'initiatePrivy2Check', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'initiatePrivy2Check',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'initiatePrivy2Check', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: setError(state.errors, 'initiatePrivy2Check', errorMessage),
      });

      // GW-7975: Re-throw the original error so the hook can differentiate error codes
      throw error;
    }
  },

  setPrivyFromAuth: (privyLevel?: PrivyLevel, privyState?: PrivyState) => {
    set({
      privyLevel: privyLevel ?? null,
      privyState: privyState ?? null,
    });
  },

  clearError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      // Clear specific operation error
      set({
        errors: clearOpError(currentState.errors, operation),
      });
    } else {
      // Clear all errors
      set({
        errors: {},
      });
    }
  },
});
